import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  takeUntil,
} from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TimezoneService } from 'src/app/core/services/timezone.service';

import { SiteService } from 'src/app/core/services/site.service';
import { ManageService } from 'src/app/core/services/manage.service';
import { AlertService } from 'src/app/core/services/alert.service';
import { CalendarService } from 'src/app/core/services/calendar.service';
import { PdfService } from 'src/app/core/services/pdf.service';
import { EventType } from '../../../../../core/enums/event-type';
import { EventDownloadModalComponent } from 'src/app/shared/modals/event-download-modal-component/event-download-modal.component';
import { EventFormModalComponent } from 'src/app/manage/activities/event-form-modal-component/event-form-modal.component';
import { CategorySelectModalComponent } from './category-select-modal/category-select-modal.component';
import { EventPageAgendaComponent } from './calendar/views/event-page-agenda/event-page-agenda.component';
import {
  invalidateActivitiesQueries,
  refetchActivitiesQueries,
} from 'src/app/shared/react-components/react-provider';

@AutoUnsubscribe()
@Component({
  selector: 'app-activities',
  templateUrl: './activities.component.html',
  styleUrls: ['./activities.component.scss'],
})
export class ActivitiesComponent implements OnInit, OnDestroy {
  @ViewChild(EventPageAgendaComponent)
  calendarComponent!: EventPageAgendaComponent;

  months: Date[] = [];
  selectedMonth: Date;
  displayedColumns: string[] = ['day', 'time', 'title', 'room'];
  private destroyed = new Subject<boolean>();
  public isSaving = new BehaviorSubject<boolean>(false);
  public eventTypes;
  public selectedMonth$ = new Subject<number>();
  public isLoading = true;
  public timezone;
  public printCalendarRestrictions: {
    monthData;
    maxEventRows;
    maxChars;
    eventRowLimits;
  };
  private siteId: number;

  constructor(
    public siteService: SiteService,
    public manageService: ManageService,
    public calendarService: CalendarService,
    private pdfService: PdfService,
    private alert: AlertService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private timezoneService: TimezoneService,
  ) {}

  get currentSiteId(): number {
    return this.siteId;
  }

  ngOnInit() {
    this.siteId = +this.route.snapshot.params['siteId'];
    this.eventTypes = EventType;
    this.siteService.site
      .pipe(
        filter((site) => site !== null),
        distinctUntilKeyChanged('id'),
        takeUntil(this.destroyed),
      )
      .subscribe((site) => {
        if (!site) {
          return;
        }
        this.timezone = site.timezone;
        this.months = this.timezoneService.generateMonthOptions(site.timezone);
        this.selectedMonth = this.months[12];
        this.updatePrintCalendarRestrictions(this.selectedMonth);
        this.isLoading = false;
      });

    // Debounce changing selected month too quickly
    this.selectedMonth$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed),
      )
      .subscribe((month: number) => {
        this.changeSelectedMonth(month);
      });
  }

  ngOnDestroy() {
    // Adding this to manually unsubscribe from the subscriptions in ngOnInit();
    // @AutoUnsubscribe didn't appear to be working for those.
    this.destroyed.next(true);
  }

  public showPrintDialog(monthDateTime: Date): void {
    const dialogRef = this.dialog.open(CategorySelectModalComponent, {
      minWidth: '400px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const subcategoryIds = result.includes('All') ? undefined : result;
        const site = this.siteService.currentSite;
        // selectedMonth is already in site timezone from generateMonthOptions
        this.pdfService.downloadCalendarPdf(
          this.selectedMonth.getMonth(),
          this.selectedMonth.getFullYear(),
          monthDateTime,
          { subcategoryIds },
        );
      }
    });
  }

  public openCalendarPdf(): void {
    const site = this.siteService.currentSite;
    // selectedMonth is already in site timezone from generateMonthOptions
    const selectedMonthIndex = this.selectedMonth.getMonth();
    const selectedYear = this.selectedMonth.getFullYear();
    this.pdfService.openActivityCalendarPdf(
      selectedMonthIndex,
      Number(selectedYear),
      this.selectedMonth,
    );
  }

  /**
   * Generate months array for month selector.
   * Create Date objects representing the first day of each month.
   * When used for an API call, the Angular service is responsible for converting to UTC.
   */
  // DEPRECATED: Use TimezoneService.generateMonthOptions instead
  private generateDateOptions(timezone: string) {
    return this.timezoneService.generateMonthOptions(timezone);
  }

  public handleMonthSelect(newMonthIndex: number) {
    this.isLoading = true;
    this.selectedMonth = this.months[newMonthIndex];
    this.selectedMonth$.next(newMonthIndex);
  }

  public changeSelectedMonth(month: number) {
    this.updatePrintCalendarRestrictions(this.selectedMonth);
    this.isLoading = false;
  }

  public async updatePrintCalendarRestrictions(selectedMonth: Date) {
    // Determine how many rows are in this month
    const monthData = await this.calendarService.evaluateCalendarFormatForMonth(
      selectedMonth,
    );

    // how many event title rows will fit in a day
    const maxEventRows = this.pdfService.calculateRowsInCalRow(
      monthData.rowsRequired,
    );

    // given the max characters that fit in a row based on default page width...
    const maxChars = this.pdfService.getMaxNumCharacters(
      // @ts-ignore
      this.pdfService.selectedPageSize.width,
    );

    // limits are based on the 'small' font size because
    // a quantity of events over a certain threshold will always trigger the smaller font
    const eventRowLimits = maxEventRows.numLinesPerDay.small;
    this.printCalendarRestrictions = {
      monthData,
      maxEventRows,
      maxChars,
      eventRowLimits,
    };
  }

  public addEvent(): void {
    const dialogRef = this.dialog.open(EventFormModalComponent, {
      width: '80vw',
      height: '660px',
      data: {
        selectedMonth: this.selectedMonth,
        months: this.months,
      },
    });

    dialogRef.afterClosed().subscribe(async (refreshNeeded) => {
      if (refreshNeeded) {
        await this.refreshCalendar();
      }
    });
  }

  public showDownloadDialog(): void {
    const site = this.siteService.currentSite;
    // selectedMonth is already in site timezone from generateMonthOptions
    const selectedMonthIndex = this.selectedMonth.getMonth();
    const selectedYear = this.selectedMonth.getFullYear();

    this.dialog.open(EventDownloadModalComponent, {
      width: '500px',
      data: {
        selectedMonthIndex: selectedMonthIndex,
        selectedYear: selectedYear,
        site: site,
        timezone: site.timezone,
      },
    });
  }

  /**
   * Refresh the calendar data using multiple strategies for maximum reliability
   */
  public async refreshCalendar(): Promise<void> {
    try {
      // Strategy 1: Use React Query cache invalidation (fastest)
      invalidateActivitiesQueries(this.siteId);

      // Strategy 2: Use component-level reload if available
      if (this.calendarComponent) {
        this.calendarComponent.forceReload();
      }
    } catch (error) {
      console.error('Failed to refresh calendar:', error);
      // Fallback to direct React Query refresh if other methods fail
      try {
        await refetchActivitiesQueries(this.siteId);
      } catch (fallbackError) {
        console.error('Fallback refresh also failed:', fallbackError);
      }
    }
  }
}
